import React, { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ArrowLeft, Check } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { adminGetPlanFeatureSettingsAdminPlanFeatureSettingsGetOptions } from '@/generated/@tanstack/react-query.gen'
import LoadingAnimate from '@/components/loading-animate'

interface PlanFeature {
  key: string
  name: string
  advanced: boolean | number | string
  professional: boolean | number | string
  enterprise: boolean | number | string
}

// Convert DTO to feature array for display
function getPlanFeatureDTO(data: any): PlanFeature[] {
  const { settings, labels } = data
  return Object.keys(labels).map((key) => {
    // Special handling for number fields
    if (
      key === 'number_of_custom_ranches' ||
      key === 'number_of_reports_per_month'
    ) {
      const advanced =
        settings.advanced[key] === -1
          ? 'Unlimited'
          : settings.advanced[key] || 0

      const professional =
        settings.professional[key] === -1
          ? 'Unlimited'
          : settings.professional[key] || 0

      const enterprise =
        settings.enterprise[key] === -1
          ? 'Unlimited'
          : settings.enterprise[key] || 0

      return {
        key,
        name: labels[key],
        advanced,
        professional,
        enterprise,
      }
    }

    // Boolean fields
    return {
      key,
      name: labels[key],
      advanced: !!settings.advanced[key] === true,
      professional: !!settings.professional[key] === true,
      enterprise: !!settings.enterprise[key] === true,
    }
  })
}

interface Props {
  open: boolean
  onClose: () => void
}

export default function TrialPlansComparisonModal({ open, onClose }: Props) {
  const {
    data: features,
    isLoading,
    refetch,
  } = useQuery({
    ...adminGetPlanFeatureSettingsAdminPlanFeatureSettingsGetOptions(),
    enabled: false,
    select: (data: any) => getPlanFeatureDTO(data),
  })

  useEffect(() => {
    if (open) refetch()
  }, [refetch, open])

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          showClose={true}
          className="!p-8 rounded-lg max-w-4xl max-h-[80vh] md:gap-6"
        >
          <div className="h-[500px] flex items-center justify-center">
            <LoadingAnimate iconClassName="w-16" />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        showClose={true}
        autoFocus={false}
        className="!p-8 rounded-lg max-w-4xl max-h-[80vh] md:gap-6"
      >
        <DialogHeader>
          <DialogTitle className="md:text-[32px] flex items-center gap-4">
            <ArrowLeft className="h-6 w-6 cursor-pointer" onClick={onClose} />
            Plans Comparison
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[60vh]">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                <TableHead className="w-[300px] text-[#202224] font-bold">
                  Hydra Score
                </TableHead>
                <TableHead className="text-center w-[300px] text-[#202224]">
                  Advanced
                </TableHead>
                <TableHead className="text-center w-[300px] text-[#202224]">
                  Professional
                </TableHead>
                <TableHead className="text-center w-[300px] text-[#202224]">
                  Enterprise
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features?.map((feature) => (
                <TableRow key={feature.name}>
                  <TableCell className="font-medium w-[300px]">
                    {feature.name}
                  </TableCell>
                  <TableCell className="text-center w-[300px]">
                    {renderFeatureCell(feature.advanced)}
                  </TableCell>
                  <TableCell className="text-center w-[300px]">
                    {renderFeatureCell(feature.professional)}
                  </TableCell>
                  <TableCell className="text-center w-[300px]">
                    {renderFeatureCell(feature.enterprise)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const renderFeatureCell = (featureValue: string | number | boolean) => {
  if (typeof featureValue === 'boolean') {
    return featureValue ? (
      <Check className="h-4 w-4 text-primary mx-auto" />
    ) : null
  }
  return featureValue
}
